import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, LogIn, Leaf, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const LoginScreen = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect') || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      // Ensure redirect doesn't cause double slashes
      const target = redirect.startsWith('/') ? redirect : `/${redirect}`;
      navigate(target);
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-primary-50 flex items-center justify-center p-4 py-20">
      <div className="max-w-md w-full bg-white rounded-[40px] shadow-2xl p-10 space-y-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-primary-100 rounded-full -mr-16 -mt-16 blur-2xl opacity-50" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-primary-100 rounded-full -ml-16 -mb-16 blur-2xl opacity-50" />

        <div className="text-center relative">
          <div className="bg-primary-600 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl shadow-primary-200">
            <Leaf className="text-white h-8 w-8" />
          </div>
          <h1 className="text-3xl font-bold text-secondary-900">Welcome Back</h1>
          <p className="text-secondary-500 mt-2">Sign in to your account</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-2xl text-sm font-medium relative">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6 relative">
          <div className="space-y-2">
            <label className="text-sm font-bold text-secondary-700 ml-1">Email Address</label>
            <div className="relative">
              <Mail className="absolute left-4 top-3.5 text-secondary-400 h-5 w-5" />
              <input type="email" placeholder="you@example.com" required
                className="w-full bg-secondary-50 border border-secondary-100 rounded-2xl py-3.5 pl-12 pr-4 focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-bold text-secondary-700 ml-1">Password</label>
            <div className="relative">
              <Lock className="absolute left-4 top-3.5 text-secondary-400 h-5 w-5" />
              <input type="password" placeholder="••••••••" required
                className="w-full bg-secondary-50 border border-secondary-100 rounded-2xl py-3.5 pl-12 pr-4 focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
          </div>

          <button disabled={loading} className="w-full bg-primary-600 hover:bg-primary-700 text-white py-4 rounded-2xl font-bold transition-all shadow-xl shadow-primary-200 flex items-center justify-center space-x-3 disabled:opacity-50">
            <LogIn className="h-5 w-5" />
            <span>{loading ? 'Signing in...' : 'Login'}</span>
          </button>
        </form>

        <div className="text-center relative">
          <p className="text-secondary-600">
            Don't have an account?{' '}
            <Link to={`/register?redirect=${redirect}`} className="text-primary-600 font-bold hover:text-primary-700 transition-colors inline-flex items-center space-x-1">
              <span>Sign Up</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
